import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { updateSegment } from '../../../redux/currentSearch/actions';
import {
    getDestinationErrorSelector,
    getActiveProduct,
    isMultipleSegments,
    getDestinationHotelErrorSelector,
    getSegmentConfig,
} from '../../../redux/currentSearch/selectors';
import { useSegmentContext } from './SegmentManager/Context';
import { EntryPoint, LocationHotelsSuppliers, SegmentConfigType, SegmentPropDefault } from '../../../models';
import { ABUtils } from '../../../utils';

// The destination lives in a shared currentSearch, so a field must ignore one it
// cannot search with instead of displaying its name: hotels only take their own
// supplier ids, DP in the SE-3806 variant B takes geo ids, trains need a
// geoNodeId, DP resolves its arrival against the hotel product so it takes either
// id, and flights need an iata.
const isDestinationUsable = (activeProduct, destination) => {
    if (!destination) {
        return true;
    }
    if (activeProduct === EntryPoint.HOTELS) {
        return !destination.geoNodeId
            && destination.geoNodeIdSupplier === LocationHotelsSuppliers.HSA;
    }
    if (activeProduct === EntryPoint.DP && !!ABUtils.getABs().isDpGeoRegionAutocomplete) {
        return true;
    }
    if (activeProduct === EntryPoint.TRAINS) {
        return !!destination.geoNodeId;
    }
    if (activeProduct === EntryPoint.DP) {
        return !!destination.iata || !!destination.geoNodeId;
    }
    return !!destination.iata;
};

const SegmentArrival = ({
    children,
    updateSegment,
    destinationError,
    destinationHotelError,
    activeProduct,
    isMultipleSegments,
    segmentConfig,
}) => {
    const { segment } = useSegmentContext();
    const isUnusableDestination = !isDestinationUsable(activeProduct, segment.destination);
    const value = isUnusableDestination ? undefined : segment.destination;

    useEffect(() => {
        if (isUnusableDestination) {
            updateSegment({ destination: undefined }, segment.id);
        }
    }, [isUnusableDestination, segment.id, updateSegment]);
    const error = activeProduct === EntryPoint.HOTELS ? destinationHotelError : destinationError;
    const isDisabled = !segmentConfig.destination.enabled;

    const setValue = (destination) => {
        if (value !== destination) {
            updateSegment({ destination }, segment.id);
        }
    };

    return children({ error, setValue, value, activeProduct, isMultipleSegments, isDisabled });
};

SegmentArrival.propTypes = {
    children: PropTypes.func.isRequired,
    updateSegment: PropTypes.func.isRequired,
    segmentConfig: SegmentConfigType.isRequired,
};

SegmentArrival.defaultProps = {
    children: null,
    updateSegment: () => {},
    segmentConfig: SegmentPropDefault,
};

const mapStateToPropsSelector = createStructuredSelector({
    destinationError: getDestinationErrorSelector,
    destinationHotelError: getDestinationHotelErrorSelector,
    activeProduct: getActiveProduct,
    isMultipleSegments: isMultipleSegments,
    segmentConfig: getSegmentConfig,
});

export const ConnectedSegmentArrival = connect(mapStateToPropsSelector, {
    updateSegment,
})(SegmentArrival);
